// PRC-020 · B3/B4: banda de prueba con días y uso. Una sola banda, la más urgente.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, PricingAccess } from '../../../context/AuthContext';
import { Meter, Notice, TopBanner } from './JourneyBlocks';
import { JourneyButton } from './JourneyBasics';

const DAY_MS = 86_400_000;
export const DEFAULT_TRIAL_LIMIT = 1000;
const NEAR_LIMIT = 900;

// Separador de miles también en números de 4 cifras («1.000», como los bocetos).
export const formatCount = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const nf = { format: formatCount };
const shortDate = (d: Date) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
const dayMonth = (d: Date) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
const time = (d: Date) => d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

export const trialUsage = (access: PricingAccess | null) => ({
  used: access?.trial_optimizations_used ?? 0,
  limit: access?.trial_optimization_limit ?? DEFAULT_TRIAL_LIMIT,
});

// Aviso al intentar optimizar con todas las optimizaciones usadas (B4).
export const TrialLimitNotice: React.FC = () => {
  const { pricingAccess, isPricingAdmin } = useAuth();
  const { limit } = trialUsage(pricingAccess);
  const end = pricingAccess?.trial_ends_at ? dayMonth(new Date(pricingAccess.trial_ends_at)) : null;
  return (
    <Notice tone="warn">
      Has usado las {nf.format(limit)} optimizaciones de la prueba. Margin Center y el resto de pantallas siguen disponibles{end ? ` hasta el ${end}` : ''}.
      {isPricingAdmin
        ? <> Para seguir optimizando, <a href="/subscription">elige un plan</a>.</>
        : ' Para seguir optimizando, el administrador de tu cuenta tiene que elegir un plan.'}
    </Notice>
  );
};

const TrialBanner: React.FC = () => {
  const { pricingAccess, isPricingAdmin } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(handle);
  }, []);

  if (pricingAccess?.state !== 'trial' || !pricingAccess.trial_ends_at) return null;

  const end = new Date(pricingAccess.trial_ends_at);
  const remainingMs = end.getTime() - now;
  if (remainingMs <= 0) return null;
  const days = Math.ceil(remainingMs / DAY_MS);
  const lastDay = end.toDateString() === new Date(now).toDateString();
  const { used, limit } = trialUsage(pricingAccess);
  const usage = `${nf.format(used)} de ${nf.format(limit)} optimizaciones`;

  const choose = isPricingAdmin
    ? <JourneyButton kind="amber" small onClick={() => navigate('/subscription')}>Elegir plan</JourneyButton>
    : undefined;

  if (lastDay) {
    return <TopBanner tone="amber" title={`Tu prueba termina hoy a las ${time(end)}`} details={['Después, el acceso se pausa hasta que contrates. No se borra nada.']} action={choose} />;
  }
  if (used >= NEAR_LIMIT) {
    return (
      <TopBanner
        tone="amber"
        title={`Has usado ${nf.format(used)} de las ${nf.format(limit)} optimizaciones de la prueba`}
        details={[<><Meter value={(used / limit) * 100} tone="amber" />Al llegar a {nf.format(limit)} podrás seguir consultando, pero no optimizar.</>]}
        action={choose}
      />
    );
  }
  if (days <= 7) {
    return <TopBanner tone="amber" title={`Tu prueba termina en ${days} ${days === 1 ? 'día' : 'días'}, el ${dayMonth(end)}`} details={['Tus datos se mantienen al contratar.']} action={choose} />;
  }
  return (
    <TopBanner
      tone="info"
      title="Prueba gratuita"
      details={[`Quedan ${days} días · hasta el ${shortDate(end)}`, usage]}
      action={isPricingAdmin ? <JourneyButton kind="ghost" small onClick={() => navigate('/subscription')}>Ver planes</JourneyButton> : undefined}
    />
  );
};

export default TrialBanner;
